import React from 'react';
import { Briefcase, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';

const BookingsTab = ({ bookings, staff, onUpdate }) => {
  const handleStatusChange = async (id, status) => {
    try {
      await api.admin.updateBookingStatus(id, status);
      toast.success('Booking status updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (bookingId, estimatorId) => {
    try {
      await api.admin.assignEstimator(bookingId, estimatorId);
      toast.success('Estimator assigned');
      onUpdate();
    } catch (error) {
      toast.error('Failed to assign estimator');
    }
  };

  const handleConvertToJob = async (bookingId) => {
    try {
      const res = await api.admin.createJobFromBooking(bookingId);
      toast.success(`Job created: ${res.data.job_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await api.admin.deleteBooking(id);
      toast.success('Booking deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const staffList = staff || [];

  return (
    <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden" data-testid="bookings-tab">
      <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="text-left text-white/60 text-sm font-medium p-4">Customer</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Service</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Date & Time</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Estimator</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Status</th>
              <th className="text-left text-white/60 text-sm font-medium p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td className="p-4">
                  <p className="text-white font-medium">{booking.customer_name}</p>
                  <p className="text-white/60 text-sm">{booking.customer_email}</p>
                </td>
                <td className="p-4 text-white/80">{booking.service_type}</td>
                <td className="p-4">
                  <p className="text-white/80">{booking.preferred_date}</p>
                  <p className="text-white/60 text-sm">{booking.preferred_time}</p>
                </td>
                <td className="p-4">
                  <Select
                    value={booking.assigned_estimator_id || "unassigned"}
                    onValueChange={(value) => handleAssign(booking.id, value === "unassigned" ? "" : value)}
                  >
                    <SelectTrigger className="w-36 bg-primary-dark border-white/20 text-white text-xs">
                      <SelectValue placeholder="Assign..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {staffList.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: s.color }} />
                            {s.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <Select 
                    value={booking.status} 
                    onValueChange={(value) => handleStatusChange(booking.id, value)}
                  >
                    <SelectTrigger className="w-32 bg-primary-dark border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <div className="flex gap-1">
                    <Button
                      onClick={() => handleConvertToJob(booking.id)}
                      variant="ghost"
                      size="sm"
                      className="text-electric-teal hover:text-electric-teal/80 text-xs"
                      title="Convert to Job"
                      data-testid={`convert-job-${booking.id}`}
                    >
                      <Briefcase className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(booking.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BookingsTab;
